import type { ClientActivity, ServerGuestStatus } from "@endclock/shared";

export const PUBLIC_HOME_URL = "https://endclock.com/";

/** No timer, render, socket, incoming reply or synthetic event can report human activity. */
export function activityKind(event: Event, gameplayPointerLocked = typeof document !== "undefined" && document.pointerLockElement?.id === "game-canvas"): ClientActivity["kind"] | null {
  if (!event.isTrusted) return null;
  if (event.type === "pointermove" || event.type === "mousemove") {
    const pointer = event as MouseEvent;
    const ownsAim = gameplayPointerLocked || event.type === "pointermove" && pointer.buttons !== 0;
    return ownsAim && Math.hypot(pointer.movementX, pointer.movementY) >= 1 ? "camera" : null;
  }
  if (event.type === "wheel") {
    const wheel = event as WheelEvent;
    return wheel.deltaX || wheel.deltaY ? "camera" : null;
  }
  if (event.type === "keydown") {
    const key = event as KeyboardEvent;
    return key.repeat || ["Shift", "Control", "Alt", "Meta"].includes(key.key) ? null : "ui";
  }
  if (event.type === "pointerdown") return "interaction";
  if (event.type === "input") return "ui";
  return null;
}

export class GuestActivity {
  private lastReported = -Infinity;
  private readonly warning = document.createElement("aside");
  private readonly abort = new AbortController();
  private ended = false;

  constructor(parent: HTMLElement, report: (kind: ClientActivity["kind"]) => void) {
    this.warning.className = "guest-idle-warning";
    this.warning.id = "guest-idle-warning";
    this.warning.setAttribute("role", "status");
    this.warning.hidden = true;
    parent.append(this.warning);
    const listener = (event: Event): void => {
      const kind = activityKind(event);
      if (!kind || this.ended) return;
      this.warning.hidden = true;
      const now = performance.now();
      if (now - this.lastReported < 1_000) return;
      this.lastReported = now;
      report(kind);
    };
    for (const event of ["pointerdown", "pointermove", "mousemove", "wheel", "keydown", "input"]) {
      document.addEventListener(event, listener, { capture: true, passive: true, signal: this.abort.signal });
    }
  }

  receive(status: ServerGuestStatus): void {
    if (this.ended) return;
    if (status.reason === "IDLE_TIMEOUT") {
      this.ended = true;
      this.warning.hidden = false;
      this.warning.textContent = "Session ended after inactivity. Returning to ENDCLOCK…";
      window.setTimeout(() => location.assign(PUBLIC_HOME_URL), 900);
      return;
    }
    this.warning.hidden = status.idleRemainingMs > 10_000;
    if (!this.warning.hidden) {
      this.warning.textContent = `INACTIVE · Session ends in ${Math.max(0, Math.ceil(status.idleRemainingMs / 1_000))} seconds.`;
    }
  }

  dispose(): void { this.abort.abort(); this.warning.remove(); }
}
